import React, { useRef, useState, useEffect } from "react";
import { Sparkles, Send, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { STYLIST_URL } from "@/lib/api";

const SUGGESTIONS = [
  "Build me a full LEVINCHI fit under $200",
  "What goes with the Midnight In Paris tee?",
  "Style a set for a night out",
];

export const AIStylist = () => {
  const { stylistOpen, setStylistOpen, guestId } = useStore();
  const [messages, setMessages] = useState([
    { role: "assistant", content: "I'm your LÉVINCHI stylist. Tell me the vibe, the budget, or the occasion — I'll build the fit." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(STYLIST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, history, session_id: guestId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const data = JSON.parse(line.slice(5).trim());
          if (data.error) {
            setMessages((prev) => {
              const cp = [...prev];
              cp[cp.length - 1] = { role: "assistant", content: "The stylist is taking a breather — try that again in a moment." };
              return cp;
            });
            continue;
          }
          if (data.delta) {
            setMessages((prev) => {
              const cp = [...prev];
              cp[cp.length - 1] = { role: "assistant", content: cp[cp.length - 1].content + data.delta };
              return cp;
            });
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const cp = [...prev];
        cp[cp.length - 1] = { role: "assistant", content: "My connection dropped — try that again in a sec." };
        return cp;
      });
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={stylistOpen} onOpenChange={setStylistOpen}>
      <SheetContent side="right" data-testid="stylist-panel" className="bg-[#0A0A0A] text-[#F4F2ED] border-l border-white/10 w-full sm:max-w-md p-0 flex flex-col grain">
        <SheetTitle className="sr-only">AI Stylist</SheetTitle>
        <div className="relative z-10 flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#FFFFFF]" />
            <span className="font-display uppercase tracking-widest text-sm">AI Stylist</span>
          </div>
          <button onClick={() => setStylistOpen(false)} aria-label="Close"><X size={18} /></button>
        </div>

        <div ref={scrollRef} className="relative z-10 flex-1 overflow-y-auto px-6 py-6 space-y-5 hide-scrollbar">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div data-testid={`stylist-msg-${m.role}`} className={`max-w-[85%] px-4 py-3 font-body text-sm leading-relaxed ${m.role === "user" ? "bg-[#F4F2ED] text-[#0A0A0A]" : "bg-white/5 border border-white/10 text-[#F4F2ED]"}`}>
                {m.content || <span className="inline-flex gap-1"><Dot /><Dot d={0.2} /><Dot d={0.4} /></span>}
              </div>
            </div>
          ))}
        </div>

        {messages.length <= 1 && (
          <div className="relative z-10 px-6 pb-3 flex flex-col gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} data-testid="stylist-suggestion" className="text-left font-body text-xs text-white/70 border border-white/15 px-4 py-2.5 hover:bg-white/5 transition-colors">{s}</button>
            ))}
          </div>
        )}

        <div className="relative z-10 px-6 py-4 border-t border-white/10 flex gap-2">
          <input
            data-testid="stylist-input"
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask your stylist…"
            className="flex-1 bg-transparent border-b border-white/30 py-2 outline-none font-body text-sm placeholder:text-white/40 focus:border-[#FFFFFF] transition-colors"
          />
          <button onClick={() => send()} disabled={busy} data-testid="stylist-send" className="text-[#FFFFFF] disabled:opacity-40" aria-label="Send"><Send size={18} /></button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Dot = ({ d = 0 }) => (
  <span className="w-1.5 h-1.5 bg-white/50 rounded-full inline-block animate-bounce" style={{ animationDelay: `${d}s` }} />
);
